import * as React from 'react'
import { useEffect, useMemo, useState } from 'react'
import { Flex, Spinner } from '@radix-ui/themes'
import { IconChevronDown } from '@tabler/icons-react'
import { useGetStudentTests } from '@/services/studentService'
import { cn } from '@/lib/utils'
import { getMarksPercentage, getMarksStyle } from '@/utils/style'
import { useDebounce } from '@/hooks/useDebounce'
import SearchBox from '@/components/ui/SearchBox'
import { Button } from '@/components/ui/button'
import { Card, CardContent } from '@/components/ui/card'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import TablePaginate from '@/components/ui/table-paginate'
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs'
import MockTestResult from '@/features/marking/components/result-drawer'
import { StudentMarkTab, TestTypeItems } from '../constants'

interface MarkTableProps {
  classId: string
  studentId: string
  className?: string
  title?: string
  tableTab: string
  moduleId: string
  setTableTab: (tab: string) => void
}

export const MarkTable: React.FC<MarkTableProps> = ({
  classId,
  studentId,
  className,
  tableTab,
  setTableTab,
}) => {
  const [page, setPage] = useState(1)
  const [type, setType] = useState('all')
  const [query, setQuery] = useState('')
  // const [result, setResult] = useState('all')
  const [resultOpen, setResultOpen] = useState(false)
  const [testId, setTestId] = useState(0)

  const debouncedQuery = useDebounce(query)

  const { studentTests, isLoadingStudentTest, meta } = useGetStudentTests({
    classId,
    studentId,
    page,
    isPracTest: tableTab === StudentMarkTab.practice_test ? 'true' : 'false',
    search: debouncedQuery.trim(),
    creatorType: type,
  })

  const filterTitle = useMemo(() => {
    return TestTypeItems.find((item) => item.value === type)?.label
  }, [type])

  // const filterResultTitle = useMemo(() => {
  //   return TestResultItems.find((item) => item.value === result)?.label
  // }, [result])

  useEffect(() => {
    setQuery('')
    setType('all')
  }, [tableTab])

  return (
    <>
      <Card className='w-full gap-4 border-none py-5'>
        <p className='ml-4 text-2xl text-[#002652]'>Tests Taken</p>
        <div className='border-b border-[#EAECF0]'>
          <Tabs
            value={tableTab}
            onValueChange={setTableTab}
            defaultValue={StudentMarkTab.mock_exam}
            className='w-full flex-col gap-3 overflow-auto px-4'
          >
            <TabsList className='gap-1 bg-transparent'>
              {Object.values(StudentMarkTab).map((item: string) => (
                <TabsTrigger
                  value={item}
                  key={item}
                  className='max-h-10 text-base data-[state=inactive]:text-[#667085]'
                >
                  {item}
                </TabsTrigger>
              ))}
            </TabsList>
          </Tabs>
        </div>
        <CardContent className='p-0'>
          <div
            className={cn(
              'space-y-6 rounded-lg bg-[#FFFFFF] p-4 !pt-0',
              className
            )}
          >
            <div className='flex items-center justify-between'>
              <SearchBox
                placeHolder='Search by Test Name'
                containerClass='max-h-10'
                onValueChange={(value: string) => setQuery(value)}
              />
              <div className='flex items-center gap-2.5 rounded-lg p-1'>
                {/* result filter  */}
                {/* <DropdownMenu>
                  <DropdownMenuTrigger
                    value={result}
                    className='w-full bg-transparent'
                    disabled
                  >
                    <Button
                      disabled
                      className='inline-flex h-10 items-center gap-1 border border-gray-300 bg-white px-3 text-black lg:px-4'
                    >
                      <span>Result: {filterResultTitle}</span>
                      <IconChevronDown className='size-4' />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent>
                    {TestResultItems.map((item) => (
                      <DropdownMenuItem
                        onClick={() => setResult(item.value)}
                        key={item.value}
                        className='cursor-pointer px-4 py-2 hover:bg-gray-100'
                      >
                        {item.label}
                      </DropdownMenuItem>
                    ))}
                  </DropdownMenuContent>
                </DropdownMenu> */}
                {/* filter fype */}
                <DropdownMenu>
                  <DropdownMenuTrigger value={type} className='bg-transparent'>
                    <Button className='inline-flex h-10 w-full items-center gap-1 border border-gray-300 bg-white px-3 text-black lg:px-4'>
                      <span>Type: {filterTitle}</span>
                      <IconChevronDown className='size-4' />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent>
                    {TestTypeItems.map((item) => (
                      <DropdownMenuItem
                        onClick={() => setType(item.value)}
                        key={item.value}
                        className='cursor-pointer px-4 py-2 hover:bg-gray-100'
                      >
                        {item.label}
                      </DropdownMenuItem>
                    ))}
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>
            </div>
          </div>
          <Table>
            <TableHeader>
              <TableRow className='bg-[#F2F4F7]'>
                <TableHead className='font-mundial px-6 text-sm font-[600] text-[#002652]'>
                  Name
                </TableHead>
                <TableHead className='font-mundial text-center text-sm font-[600] text-[#002652]'>
                  Attempts
                </TableHead>
                <TableHead className='font-mundial text-center text-sm font-[600] text-[#002652]'>
                  Unmarked
                </TableHead>
                <TableHead className='font-mundial text-center text-sm font-[600] text-[#002652]'>
                  <div className='flex items-center justify-center gap-1 text-center'>
                    Average AI Mark
                  </div>
                </TableHead>
                <TableHead className='font-mundial w-[10%] text-center text-sm font-[600] text-[#002652]'>
                  Average Tutor Mark
                </TableHead>
                {/* <TableHead className='font-mundial text-center text-sm font-[600] text-[#002652]'>
                  Results
                </TableHead> */}
                <TableHead className='font-mundial text-sm font-[600] text-[#002652]'></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody className='text-[#002652]'>
              {isLoadingStudentTest ? (
                <TableRow>
                  <TableCell colSpan={6}>
                    <Flex justify={'center'}>
                      <Spinner />
                    </Flex>
                  </TableCell>
                </TableRow>
              ) : (
                <>
                  {studentTests?.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={8} className='h-24 text-center'>
                        No data found.
                      </TableCell>
                    </TableRow>
                  )}
                  {studentTests.map((item) => (
                    <TableRow className='hover:bg-gray-50'>
                      <TableCell className='w-[167px] px-6'>
                        <div>
                          <div className='w-[100px] font-[400] text-[#002652]'>
                            {item.name}
                          </div>
                          <div className='text-xs font-[300] text-[#475467]'>
                            {item.owner_info.full_name}
                          </div>
                        </div>
                      </TableCell>
                      <TableCell className='w-[15%] text-center text-sm font-[300] text-[#002652]'>
                        {item.total_attempts}
                      </TableCell>
                      <TableCell className='w-[15%] text-center text-sm font-[300] text-[#002652]'>
                        {item.unmarked}
                      </TableCell>
                      <TableCell
                        className={cn(
                          'w-[10%] items-center text-center text-sm font-[300]',
                          getMarksStyle(item?.avg_ai_marks)
                        )}
                      >
                        {getMarksPercentage(item.avg_ai_marks)}
                      </TableCell>

                      <TableCell
                        className={cn(
                          'w-[10%] items-center text-center text-sm font-[300]',
                          getMarksStyle(item?.avg_tutor_marks)
                        )}
                      >
                        {getMarksPercentage(item?.avg_tutor_marks)}
                      </TableCell>
                      {/* <TableCell className='w-[10%] text-center'>
                        {item.result}
                      </TableCell> */}
                      <TableCell className='w-[10%] px-3 text-center'>
                        <p
                          onClick={() => {
                            setTestId(item.id)
                            setResultOpen(true)
                          }}
                          className='cursor-pointer rounded px-3 py-2 text-[#0057B8] hover:bg-[#0057B8]/10'
                        >
                          View detail
                        </p>
                      </TableCell>
                    </TableRow>
                  ))}
                </>
              )}
            </TableBody>
          </Table>

          {meta && meta.total_pages > 1 && (
            <div className='flex justify-end px-8 py-4'>
              <TablePaginate
                itemPerPage={meta.items_per_page}
                totalItems={meta.total_items}
                page={page}
                totalPages={meta.total_pages}
                onPageChange={setPage}
                readOnlyPageDisplay={false}
                hideOnSinglePage={false}
              />
            </div>
          )}
        </CardContent>

        {resultOpen && (
          <MockTestResult
            isOpen={true}
            onClose={() => setResultOpen(false)}
            practiceTestId={String(testId || '')}
            studentId={Number(studentId)}
          />
        )}
      </Card>
    </>
  )
}

export default MarkTable
